import { Repository } from './Repository';
import { Event } from '../models/Event';
import { FakeData } from './FakeData';
import { Pleb } from '../models/Pleb';

export interface EventUpdateDto {
  name?: string;
  startDate?: string;
  endDate?: string;
  venue?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
  timeZone?: string;
}

export class EventsRepository extends Repository {
  async deleteEvent(eventId: string): Promise<void> {
    const path = `v1/events/${eventId}`;
    await Repository.client.delete(path);
  }

  async getEventDetails(eventId: string, currentUserId: string): Promise<Event | null> {
    if(Repository.offlineMode) {
      return FakeData.getEvent();
    }

    const path = `v1/events/${eventId}`;
    const response = await Repository.client.get(path);
    return Event.fromJson(response.data.data.event, response.data.data.userRsvp, currentUserId);
  }

  async getEventsNearby(
    latitude: number, 
    longitude: number, 
    filter: string | null,
    currentUserId: string, 
    page: number, 
    itemsPerPage: number
  ): Promise<{data: Event[], count: number}> {
    if(Repository.offlineMode) {
      return {
        data: [FakeData.getEvent()],
        count: 1
      };
    }

    let path = `v1/events?latitude=${latitude}&longitude=${longitude}&kind=upcoming&published=true&sortBy=distance&page=${page}&limit=${itemsPerPage}`;
    
    if(filter) {
      path += `&filter=${filter}`;
    } else {
      path += `&radius=${50000}`;
    }
    
    const response = await Repository.client.get(path);
    return {
      data: response.data.data.events.map((event: any) => Event.fromJson(event.event, event.userRsvp, currentUserId)),
      count: response.data.data.count
    };
  }

  async getEventsCreatedByUser(
    userId: string, 
    currentUserId: string, 
    page: number, 
    itemsPerPage: number
  ): Promise<{data: Event[], count: number}> {
    if(Repository.offlineMode) {
      return {
        data: [FakeData.getEvent()],
        count: 1
      };
    }

    const path = `v1/events?createdById=${userId}&kind=upcoming&page=${page}&limit=${itemsPerPage}`;
    const response = await Repository.client.get(path);
    return {
      data: response.data.data.events.map((event: any) => Event.fromJson(event.event, null, currentUserId)),
      count: response.data.data.count
    };
  }

  async joinEventGroupChat(eventId: string, currentUserId: string): Promise<void> {
    const path = `v1/events/${eventId}/chat/members/${currentUserId}`;
    await Repository.client.put(path);
  }

  async rsvpEvent(eventId: string, currentUserId: string): Promise<Event> {
    const path = `/v1/events/${eventId}/rsvps`;
    const response = await Repository.client.post(path);
    return Event.fromJson(response.data.data.event, response.data.data.userRsvp, currentUserId);
  }

  async getParticipants(eventId: string, currentUserId: string, page: number = 0, limit: number = 100): Promise<{members: Pleb[], hasMore: boolean}> {
    const path = `v1/events/${eventId}/rsvps?page=${page}&limit=${limit}`;
  
    try {
      const response = await Repository.client.get(path);
      return {
        members: (response.data.data.rsvps as Array<any>)
          .map(member => Pleb.fromJson(member, currentUserId || '' ))
          .filter(member => member !== null) as Pleb[],
        hasMore: response.data.data.rsvps.length > 0
      };
    } catch (err) {
      throw err;
    }
  }

  async unrsvpEvent(eventId: string, currentUserId: string): Promise<Event> {
    const path = `/v1/events/${eventId}/rsvps`;
    const response = await Repository.client.delete(path);
    return Event.fromJson(response.data.data.event, response.data.data.event.userRsvp, currentUserId);
  }

  async getEventShareLink(eventId: string): Promise<string> {
    const path = `/v1/events/${eventId}/shareLinks`;
    const response = await Repository.client.post(path);
    return response.data.data.link;
  }

  async updateEvent(eventId: string, eventData: EventUpdateDto, currentUserId: string): Promise<Event> {
    const path = `/v1/events/${eventId}`;
    const response = await Repository.client.patch(path, eventData);
    return Event.fromJson(response.data.data, null, currentUserId);
  }

  async claimEvent(eventId: string, currentUserId: string): Promise<Event> {
    const path = `/v1/events/${eventId}`;
    const response = await Repository.client.patch(path, {
      createdBy: currentUserId
    });
    return Event.fromJson(response.data.data, null, currentUserId);
  }

  async addressAutocomplete(input: string): Promise<AddressAutocompleteItem[]> {
    const path = `/v2/geo/autocomplete?input=${encodeURIComponent(input)}`;
    const response = await Repository.client.get(path);
    return (response.data.data ?? []).map((item: any) => ({
      placeId: item.id,
      name: item.name ?? '',
    }));
  }

  async placeDetails(placeId: string): Promise<PlaceDetails> {
    const path = `/v1/geo/place-details/${placeId}`;
    const response = await Repository.client.get(path);
    return response.data.data;
  }
}

export interface AddressAutocompleteItem {
  placeId: string;
  name: string;
}

export interface PlaceDetails {
  id: string;
  name: string;
  venue: string | null;
  address: string;
  lat: number;
  lng: number;
  timeZone: string;
}

// Create a singleton instance
export const eventsRepository = new EventsRepository();
